import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreditCard } from "lucide-react";

interface AddCardFormProps {
  onCancel: () => void;
  onAddCard: (cardData: CardData) => void;
}

interface CardData {
  firstName: string;
  lastName: string;
  address: string;
  country: string;
  state: string;
  city: string;
  zip: string;
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  isDefault: boolean;
}

const COUNTRIES = [
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Netherlands",
  "Belgium",
];

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming"
];

const MONTHS = Array.from({ length: 12 }, (_, i) => 
  String(i + 1).padStart(2, '0')
);

const YEARS = Array.from({ length: 15 }, (_, i) => 
  String(new Date().getFullYear() + i)
);

export const AddCardForm = ({ onCancel, onAddCard }: AddCardFormProps) => {
  const [formData, setFormData] = useState<CardData>({
    firstName: "",
    lastName: "",
    address: "",
    country: "United States",
    state: "",
    city: "",
    zip: "",
    cardNumber: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
    isDefault: false,
  });

  const formatCardNumber = (value: string) => {
    const numbers = value.replace(/\D/g, "").slice(0, 16);
    return numbers.replace(/(.{4})/g, "$1 ").trim();
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setFormData(prev => ({ ...prev, cardNumber: formatted }));
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setFormData(prev => ({ ...prev, cvv: value }));
  };

  const isFormValid = 
    formData.firstName.trim() &&
    formData.lastName.trim() &&
    formData.cardNumber.replace(/\s/g, "").length === 16 &&
    formData.expiryMonth &&
    formData.expiryYear &&
    formData.cvv.length >= 3;

  const handleSubmit = () => {
    if (isFormValid) {
      onAddCard(formData);
    }
  };

  return (
    <div className="space-y-5">
      <h3 className="text-lg font-semibold">Add card</h3>

      {/* Name Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="font-semibold">First Name</Label>
          <Input
            placeholder="First Name"
            value={formData.firstName}
            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label className="font-semibold">Last Name</Label>
          <Input
            placeholder="Last Name"
            value={formData.lastName}
            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
          />
        </div>
      </div>

      {/* Address */}
      <div className="space-y-2">
        <Label className="font-semibold">Address</Label>
        <Input
          placeholder="Enter address..."
          value={formData.address}
          onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
        />
        <div className="grid grid-cols-4 gap-2">
          <Select
            value={formData.country}
            onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
          >
            <SelectTrigger className="bg-background">
              <SelectValue placeholder="Country" />
            </SelectTrigger>
            <SelectContent className="bg-background border border-border z-50">
              {COUNTRIES.map(country => (
                <SelectItem key={country} value={country}>{country}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={formData.state}
            onValueChange={(value) => setFormData(prev => ({ ...prev, state: value }))}
          >
            <SelectTrigger className="bg-background">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent className="bg-background border border-border z-50 max-h-60">
              {US_STATES.map(state => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="City"
            value={formData.city}
            onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
          />
          <Input
            placeholder="Zip"
            value={formData.zip}
            onChange={(e) => setFormData(prev => ({ ...prev, zip: e.target.value }))}
          />
        </div>
      </div>

      {/* Card Details */}
      <div className="space-y-2">
        <Label className="font-semibold">Card details</Label>
        <Input
          placeholder="1234 1234 1234 1234"
          value={formData.cardNumber}
          onChange={handleCardNumberChange}
        />
        <div className="grid grid-cols-3 gap-2">
          <Select
            value={formData.expiryMonth}
            onValueChange={(value) => setFormData(prev => ({ ...prev, expiryMonth: value }))}
          >
            <SelectTrigger className="bg-background">
              <SelectValue placeholder="MM" />
            </SelectTrigger>
            <SelectContent className="bg-background border border-border z-50">
              {MONTHS.map(month => (
                <SelectItem key={month} value={month}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={formData.expiryYear}
            onValueChange={(value) => setFormData(prev => ({ ...prev, expiryYear: value }))}
          >
            <SelectTrigger className="bg-background">
              <SelectValue placeholder="YYYY" />
            </SelectTrigger>
            <SelectContent className="bg-background border border-border z-50 max-h-60">
              {YEARS.map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Input
              placeholder="CVV"
              type="password"
              value={formData.cvv}
              onChange={handleCvvChange}
              className="pr-10"
            />
            <CreditCard className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>
        </div>
      </div>

      {/* Default Payment Checkbox & Buttons */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id="default-payment"
            checked={formData.isDefault}
            onCheckedChange={(checked) => 
              setFormData(prev => ({ ...prev, isDefault: checked === true }))
            }
          />
          <label htmlFor="default-payment" className="text-sm text-muted-foreground cursor-pointer">
            Make this your default payment method.
          </label>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid}
            className="bg-primary/50 hover:bg-primary"
          >
            Add card
          </Button>
        </div>
      </div>
    </div>
  );
};
